import type {
  ConceptWorksheetFilterBody,
  CustomPaperWorksheetFilterBody,
  MergedWorksheetFilterBody,
  OneClickWorksheetFilterBody,
  SchoolTestPaperWorksheetFilterBody,
  SchoolTestWorksheetFilterBody,
  WeakPeriodWorksheetFilterBody,
  WorksheetFilterDraft,
  WorksheetPdf,
} from '@mathflat/domain/MakeWorksheet'
import type { Workbook } from '@mathflat/domain/Workbook'
import type { WorksheetFilterType, WrongWorksheetFilterType } from '@mathflat/domain/Worksheet'
import { tJosa } from '@mathflat/shared/i18n/tJosa'
import qs from 'qs'
import type { ComponentProps } from 'react'
import { generatePath } from 'react-router'

import { TOAST_STR } from '@/constant'
import {
  getMakeVariant,
  getMakeVariantCapabilities,
  makeVariantSuffixFor,
} from '@/constant/makeWorksheet/makeVariantSearch'
import type { MakeVariant } from '@/constant/makeWorksheet/variant'
import type { 학습지필터 } from '@/contentComponents/makeLessonMaterial/(filter)/_deprecated/service/MakeLessonMaterialFilter.service'
import { SupportedWorkbookDetailService } from '@/contentComponents/makeLessonMaterial/(step1)/widget/_deprecated/MakeBySupportedWorkbook/service/SupportedWorkbookDetail.service'
import type { AssignedStudent } from '@/contentComponents/modal/AssignGroupStudentModal/AssignGroupStudentModal'
import AssignGroupStudentModal from '@/contentComponents/modal/AssignGroupStudentModal/AssignGroupStudentModal'
import {
  cancelPendingScheduleForEdit,
  confirmPendingScheduleEdit,
} from '@/contentComponents/modal/WorksheetAssignSchedule/_utils/pendingScheduleEditGuard'
import ReservableAssignWorksheetModal from '@/contentComponents/modal/WorksheetAssignSchedule/ReservableAssignWorksheetModal'
import { derivationController } from '@/controller/derivation.controller'
import { lessonWorksheetController } from '@/controller/lessonWorksheet.controller'
import { worksheetController } from '@/controller/worksheet.controller'
import type { MakeWorksheetPreviewOptionFilter, Student, Worksheet } from '@/dto'
import { MakeWorksheetByChapterOption } from '@/dto'
import {
  MAKE_LESSON_MATERIAL_STEP1_TYPE,
  MakeLessonMaterialCommonParams,
} from '@/dto/LessonMaterial/MakeLessonMaterialCommonParams'
import type { MyList } from '@/dto/MyList/MyList'
import type {
  ArithmeticChapterFilterParams,
  ChapterFilterParams,
  LeveledChapterFilterParams,
} from '@/dto/Worksheet/MakeWorksheetByChapterOption'
import commonRepo from '@/repository/common.repo'
import { RouteName } from '@/router'
import type {
  FixOption as FixOptionValue,
  MakeWorksheetProblemSortType,
} from '@/types/makeWorksheet'
import { commonErrorMessage, toastError, toastSuccess } from '@/utils/toast'
import { _mixpanelUtils } from '@/utils/trackings/_mixpanel'
import { trackingConverter } from '@/utils/trackings/utils/trackingConverter'
import worksheetUtil from '@/utils/Worksheet'

import modalService from './modal.service'
import { scenarioService } from './scenario.service'

export type CreateFilterIdParams =
  | {
      type: Extract<WorksheetFilterDraft['type'], 'WEAK_CONCEPT'>
      params: LeveledChapterFilterParams
    }
  | {
      type: Extract<WorksheetFilterDraft['type'], 'WEAK_PERIOD'>
      params: LeveledChapterFilterParams &
        Pick<WeakPeriodWorksheetFilterBody, 'startDate' | 'endDate'>
    }
  | {
      type: Extract<WorksheetFilterDraft['type'], 'WEAK_WORKSHEET'>
      params: ChapterFilterParams | ArithmeticChapterFilterParams
    }
  | {
      type: Extract<WorksheetFilterDraft['type'], 'SCHOOL_TEST'>
      params: SchoolTestWorksheetFilterBody
    }
  | {
      type: Extract<WorksheetFilterDraft['type'], 'CUSTOM_PAPER'>
      params: CustomPaperWorksheetFilterBody
    }
  | {
      type: Extract<WorksheetFilterDraft['type'], 'SCHOOL_TEST_PAPER'>
      params: SchoolTestPaperWorksheetFilterBody
    }
  | {
      type: Extract<WorksheetFilterDraft['type'], 'ONE_CLICK'>
      params: OneClickWorksheetFilterBody
    }
  | {
      type: Extract<WorksheetFilterDraft['type'], 'MERGED_WORKSHEET'>
      params: MergedWorksheetFilterBody
    }

class CommonWorksheet {
  moveEditWorksheetFromDerivation({
    filterId,
    assignStudentIdList,
    tag,
    prefix,
    designOption,
    problemSortType,
    download,
    entryPath,
    destinationPath,
    originPublicWorkbookId,
    weakWorkbookSourceId,
    weakWorkbookPartitionType,
  }: {
    filterId: string
    assignStudentIdList: string[]
    tag?: MakeWorksheetPreviewOptionFilter['tag']
    prefix: MakeWorksheetPreviewOptionFilter['prefix']
    designOption: Partial<WorksheetPdf>
    problemSortType?: MakeWorksheetProblemSortType
    download?: boolean
    entryPath: MakeLessonMaterialCommonParams<'COPY'>['entryContext']['entryPath']
    destinationPath?: MakeLessonMaterialCommonParams<'COPY'>['entryContext']['destinationPath']
    originPublicWorkbookId?: Workbook['id']
    weakWorkbookSourceId?: Workbook['id']
    weakWorkbookPartitionType?: 'PAGE' | 'PROBLEM'
  }) {
    const queryString = qs.stringify(
      {
        ...designOption,
        tag,
        prefix,
        assignStudentIdList,
      },
      { arrayFormat: 'comma' },
    )

    const params = new MakeLessonMaterialCommonParams<'COPY'>({
      filterId,
      problemSortType,
      download,
      entryContext: {
        entryPath,
        destinationPath,
        fixOption: 'COPY',
      },
      originPublicWorkbookId,
      weakWorkbookSourceId,
      weakWorkbookPartitionType,
    }).params

    commonRepo.router.push(`/make-worksheet/edit/${params}&${queryString}`)
  }

  // 학습지 수정
  // 학습지 필터 정보 get -> filterId생성 -> 학습지 만들기 2단계 이동
  async moveEditWorksheet<FixOption extends FixOptionValue>({
    worksheetId,
    // entryPath,
    // fixOption = 'EDIT',
    assignStudentIdList = [],
    designOption = {},
    myListId,
    entryContext,
    makeVariant = 'default',
  }: {
    worksheetId: Worksheet['id'] // 학습지 아이디
    // entryPath: MakeCommonParams<FixOption>['entryPath'] // 1단계 진입 경로
    // fixOption: FixOption // copy - 복제 후 수정 / edit - 학습지 수정 / EDIT_ASSIGNED - 출제된 학습지 수정(3단계 진입)
    assignStudentIdList?: Student['id'][]
    designOption?: Partial<WorksheetPdf>
    myListId?: MyList['id']
    entryContext: MakeLessonMaterialCommonParams<FixOption>['entryContext']
    /** entrance-test면 make-flow 저장이 creverse API로 분기된다 */
    makeVariant?: MakeVariant
  }) {
    // 출제 예약이 걸린 학습지는 수정 화면으로 넘어가기 전에 묻는다. 취소하면 그 자리에 머무른다
    const pendingSchedule = await confirmPendingScheduleEdit({
      worksheetId,
      fixOption: entryContext.fixOption,
      hasAssignedStudents: assignStudentIdList.length > 0,
    })
    if (pendingSchedule === false) return

    try {
      const data = await worksheetController.fetchWorksheetDetail(worksheetId)
      const { filter, problems, pdf, title, tag, writer, titlePrefix, school, grade } = data
      const _filter = (() => {
        if (!filter) return new MakeWorksheetByChapterOption()
        return filter
      })()
      const problemList = problems.map(({ id, tagTop, baseWorkbookProblemId, problemSource }) => ({
        id,
        tagTop,
        baseWorkbookProblemId,
        boxIndex: problemSource?.id,
      }))
      const { type, ...filterOption } = _filter

      let source: string | undefined = undefined
      if (entryContext.fixOption !== 'NONE' && entryContext.entryPath === RouteName.practiceTest) {
        source = 'PRACTICE_TEST'
      }

      const params = {
        ...filterOption,
        problemList,
        title,
        source,
      }

      const filterId = await this.createFilterId({ type, params } as CreateFilterIdParams)

      if (entryContext.fixOption === 'EDIT' && assignStudentIdList.length > 0) {
        modalService.openConfirmModal({
          content: (
            <div>
              <p>학생에게 {tJosa('출제')}된 학습지는 수정이 불가능합니다. </p>
              <p>{tJosa('출제')} 취소를 먼저 진행해주세요.</p>
            </div>
          ),
          onClick: modalService.closeModal,
          actionButtonText: '확인',
          hasCancelButton: false,
        })
        return
      }
      const param = new MakeLessonMaterialCommonParams<FixOption>({
        entryContext,
        filterId,
        sameOptionUse: 'NONE',
        filterType: type as WorksheetFilterType,
      }).params
      const queryString = qs.stringify(
        {
          title,
          worksheetId,
          tag,
          writer,
          prefix: titlePrefix,
          schoolType: school,
          grade,
          assignStudentIdList,
          ...{ ...designOption, ...pdf },
          myListId,
        },
        { arrayFormat: 'comma' },
      )
      const url =
        entryContext.fixOption === 'EDIT_ASSIGNED'
          ? `/make-worksheet/options`
          : `/make-worksheet/edit`
      const moveToUrl = `${url}/${param}&${queryString}${makeVariantSuffixFor(makeVariant)}`
      // 수정 준비가 다 된 뒤에 예약을 취소한다. 앞 단계가 실패했는데 예약만 사라지는 일을 막는다
      if (pendingSchedule && !(await cancelPendingScheduleForEdit(pendingSchedule))) return
      commonRepo.router.push(moveToUrl)
    } catch (error) {
      if (error.code === 'WRONG_REQUEST_CURRICULUM_GROUP') {
        toastError('09개정 문제가 포함된 학습지는 생성 또는 수정할 수 없습니다.')
      } else {
        commonErrorMessage(error)
      }
    }
  }

  async createFilterId<T extends WorksheetFilterType | WrongWorksheetFilterType>(
    props: T extends 'CONCEPT'
      ? { type: 'CONCEPT'; params: 학습지필터.단원유형별필터 }
      : T extends
            | 'PUBLIC_WORKBOOK'
            | 'SCHOOL_WORKBOOK'
            | 'CUSTOM_SIGNATURE_WORKBOOK'
            | 'ACADEMY_GROUP_WORKBOOK'
        ? {
            type:
              | 'PUBLIC_WORKBOOK'
              | 'SCHOOL_WORKBOOK'
              | 'CUSTOM_SIGNATURE_WORKBOOK'
              | 'ACADEMY_GROUP_WORKBOOK'
            params: 학습지필터.지원교재필터
          }
        : T extends 'PRACTICE_TEST'
          ? { type: 'PRACTICE_TEST'; params: 학습지필터.수능모의고사필터 }
          : T extends 'SCHOOL_TEST'
            ? { type: 'SCHOOL_TEST'; params: 학습지필터.학교별기출필터 }
            : T extends 'MERGED_WORKSHEET'
              ? { type: 'MERGED_WORKSHEET'; params: 학습지필터.병합학습지필터 }
              : T extends 'MY_DB_ORIGINAL'
                ? { type: 'MY_DB_ORIGINAL'; params: 학습지필터.나의DB원본필터 }
                : T extends 'MY_DB_SIMILAR'
                  ? { type: 'MY_DB_SIMILAR'; params: 학습지필터.나의DB유사필터 }
                  : CreateFilterIdParams,
  ) {
    let filterId: undefined | string = undefined

    switch (props.type) {
      case 'CONCEPT': {
        // 단원 유형 — 연산 필터는 같은 URL 이지만 전용 컨트롤러로 분기한다 (mp_event_name·요청 타입 분리)
        if ('arithmeticOnly' in props.params && props.params.arithmeticOnly) {
          filterId = await worksheetController.createFilterIdByArithmetic(
            props.params as unknown as ConceptWorksheetFilterBody,
          )
          break
        }
        filterId = await worksheetController.createFilterIdByChapter(props.params)
        break
      }
      case 'PUBLIC_WORKBOOK': // 시중교재
      case 'SCHOOL_WORKBOOK':
      case 'CUSTOM_SIGNATURE_WORKBOOK':
      case 'ACADEMY_GROUP_WORKBOOK':
        {
          filterId = await worksheetController.postFilterBySupportedWorkbook(props.params)
        }
        break
      case 'PRACTICE_TEST': // 수능 모의고사
        {
          filterId = await worksheetController.createFilterByPracticeTest(props.params)
        }
        break
      case 'WEAK_CONCEPT': {
        filterId = await derivationController.fetchFilterIdByConcept(props.params)
        break
      }
      case 'WEAK_PERIOD': {
        filterId = await derivationController.fetchFilterIdByPeriod(props.params)
        break
      }
      case 'WEAK_WORKSHEET': {
        filterId = await derivationController.fetchFilterIdByWorksheet(props.params)
        break
      }
      case 'SCHOOL_TEST': {
        filterId = await worksheetController.createFilterBySchoolTest(
          props.params as SchoolTestWorksheetFilterBody,
        )
        break
      }
      case 'CUSTOM_PAPER': {
        filterId = await worksheetController.createFilterByCustomPaper({
          filterId: props.params?.filterId,
          paperId: props.params.paperId,
          selectedPageIndexes: props.params.selectedPageIndexes,
          problemList: props.params.problemList,
          selectedConceptIdList: props.params.selectedConceptIdList,
          selectedLittleChapterIdList: props.params.selectedLittleChapterIdList,
        })
        break
      }
      case 'SCHOOL_TEST_PAPER': {
        filterId = await worksheetController.createFilterBySchoolTestPaper({
          filterId: props.params?.filterId,
          paperId: props.params.paperId,
          selectedPageIndexes: props.params.selectedPageIndexes,
          problemList: props.params.problemList,
          selectedConceptIdList: props.params.selectedConceptIdList,
          selectedLittleChapterIdList: props.params.selectedLittleChapterIdList,
        })
        break
      }

      case 'ONE_CLICK': {
        filterId = await worksheetController.createFilterByOneClick({
          ...props.params,
        } as OneClickWorksheetFilterBody)
        break
      }

      case 'MERGED_WORKSHEET': {
        filterId = await worksheetController.createFilterIdByMergedWorksheet({
          filterId: props.params.filterId,
          mergedWorksheetIds: props.params.mergedWorksheetIds,
          onlyAutoScorable: props.params.onlyAutoScorable,
          problems: (
            (
              props.params as MergedWorksheetFilterBody & {
                problemList: MergedWorksheetFilterBody['problems']
              }
            ).problemList ?? []
          ).map((p) => ({
            id: p.id,
            baseWorkbookProblemId: p.baseWorkbookProblemId,
            tagTop: p.tagTop,
          })),
          problemSortType: props.params.problemSortType,
          title: props.params.title,
        })
        break
      }

      case 'MY_DB_ORIGINAL': {
        filterId = await worksheetController.createOriginalFilterBySchoolTestPaperV2({
          filterId: props.params.filterId,
          title: props.params.title,
          myDbProblemDetailIds: props.params.myDbProblemDetailIds,
          problemList: props.params.problemList,
        })
        break
      }

      case 'MY_DB_SIMILAR': {
        try {
          filterId = await worksheetController.createSimilarFilterBySchoolTestPaperV2({
            filterId: props.params.filterId,
            title: props.params.title,
            myDbProblemDetailIds: props.params.myDbProblemDetailIds,
            problemList: props.params.problemList,
            // 서버는 생략하면 3 을 쓰고 null 은 받지 않는다
            similarX: props.params.similarX || undefined,
            similarLevel: props.params.similarLevel,
            onlyAutoScorable: props.params.onlyAutoScorable,
          })
        } catch (error) {
          if (error.code === 'VALIDATION_FAIL' && error.message?.includes('myDbProblemDetailIds')) {
            toastError('모든 원본 문제가 생성 중입니다.\n잠시 후 다시 시도해주세요.')
          }
          throw error
        }
        break
      }
    }
    return filterId
  }

  onExitMakeWorksheet<FixOption extends FixOptionValue>(
    entryContext: MakeLessonMaterialCommonParams<FixOption>['entryContext'],
    sameOptionUse = false, // 동일옵션으로 계속 만들지 유무
    makeComplete = false, // 완전히 생성했는지 여부 - 3단계 생성에만 true, 그 외에는 false - 학습지만들기를 제외한 곳에서 사용
    workbookId?: Workbook['id'],
  ) {
    // 동일 범위로 계속 만들 경우
    const makeVariant = getMakeVariant(window.location.search)
    const searchParams = qs.stringify(
      {
        makeComplete: makeComplete ? 'DONE' : 'YET',
        ...(sameOptionUse ? { sameOptionUse: 'USE' } : {}),
        entryPath: entryContext.entryPath,
        destinationPath: entryContext.destinationPath,
        // 재진입 search를 여기서 새로 조립하므로, 빠뜨리면 다음 만들기가 default 모드로 떨어진다
        ...(makeVariant !== 'default' ? { makeVariant } : {}),
      },
      { addQueryPrefix: true },
    )

    SupportedWorkbookDetailService.destroy({
      reason: 'ON_CREATE',
      sameOptionUse,
    })

    if (entryContext.fixOption === 'NONE' && sameOptionUse) {
      switch (entryContext.stepOneType) {
        // 학습지만들기 1단계 - 단원 유형별로 보낸다.
        case MAKE_LESSON_MATERIAL_STEP1_TYPE.단원유형별:
          commonRepo.router.push({
            pathname: generatePath(RouteName.makeWorksheetByChapter),
            search: searchParams,
          })
          break
        // 학습지만들기 1단계 - 시중교재로 보낸다.
        case MAKE_LESSON_MATERIAL_STEP1_TYPE.시중교재:
          if (workbookId) {
            commonRepo.router.push({
              pathname: generatePath(RouteName.makeWorksheetByWorkbookDetail, {
                workbookId,
              }),
              search: searchParams,
            })
          }
          break
        // 학습지만들기 1단계 - 수능 모의고사로 보낸다.
        case MAKE_LESSON_MATERIAL_STEP1_TYPE.수능모의고사:
          commonRepo.router.push({
            pathname: generatePath(RouteName.makeWorksheetByPracticeTest),
            search: searchParams,
          })
          break
        // 학습지만들기 1단계 - 시그니처 교재로 보낸다.
        case MAKE_LESSON_MATERIAL_STEP1_TYPE.시그니처교재:
          if (workbookId) {
            commonRepo.router.push({
              pathname: generatePath(RouteName.makeWorksheetBySignatureWorkbookDetail, {
                workbookId,
              }),
              search: searchParams,
            })
          }
          break
        // 학습지만들기 1단계 - 학교별 내신 기출로 보낸다.
        case MAKE_LESSON_MATERIAL_STEP1_TYPE.학교별기출:
          commonRepo.router.push({
            pathname: generatePath(RouteName.makeWorksheetBySchoolTest),
            search: searchParams,
          })
          break
        // 학습지만들기 1단계 - 기출업로드로 보낸다.
        case MAKE_LESSON_MATERIAL_STEP1_TYPE.기출업로드:
          commonRepo.router.push({
            pathname: generatePath(RouteName.makeWorksheetByPaper),
            search: searchParams,
          })
          break
        // 학습지만들기 1단계 - 연산으로 보낸다.
        case MAKE_LESSON_MATERIAL_STEP1_TYPE.연산:
          commonRepo.router.push({
            pathname: generatePath(RouteName.makeWorksheetByArithmetic),
            search: searchParams,
          })
          break
        default:
          commonRepo.router.push({
            pathname: generatePath(RouteName.makeWorksheetByChapter),
            search: searchParams,
          })
      }
      return
      // TODO: 즐겨찾는문제 sameoptionuse안쓰는지 한번더 체크
    }

    // 동일범위가 아닌경우
    const _pathname =
      entryContext.destinationPath ||
      entryContext.entryPath ||
      RouteName[getMakeVariantCapabilities(window.location.search).exitFallbackRouteName]
    const [pathname = '', search = ''] = _pathname.split('?')

    commonRepo.router.push({
      pathname: generatePath(pathname),
      search: search,
    })
  }
  // 학습지 일괄 출제
  assignStudentWorksheetList = (
    worksheetList: Worksheet[],
    fetchInitCallback?: (
      worksheetIdList: Worksheet['id'][],
      assignedStudentIds?: Worksheet['assignedStudentIdList'],
      unassignedStudentIds?: Worksheet['assignedStudentIdList'],
    ) => Promise<void> | void,
    onAssignClickTracking?: () => void,
  ) => {
    const worksheetIdList = worksheetList.map((worksheet) => worksheet.id)

    // 선택한 학습지들에서 공통으로 출제된 학생들의 리스트
    const getCommonAssignedLists = (worksheetList: Worksheet[]) => {
      const noDuplicatedAssignedList: AssignedStudent[] = []
      const duplicatedAssignedList: AssignedStudent[] = []

      const assignLists = worksheetList.map((worksheet) => {
        if (worksheet.assignedStudentWorksheetList) {
          return worksheet.assignedStudentWorksheetList
        }
      })

      assignLists.flat().forEach((assignedStudent) => {
        if (assignedStudent) {
          if (
            !noDuplicatedAssignedList
              .map((assigned) => assigned.studentId)
              .includes(assignedStudent.studentId)
          ) {
            noDuplicatedAssignedList.push(assignedStudent)
          }
        }
      })

      noDuplicatedAssignedList.forEach((unique) => {
        const duplicated = assignLists
          .flat()
          .filter((assign) => assign?.studentId === unique.studentId)
        if (duplicated.length === worksheetList.length) {
          duplicatedAssignedList.push(unique)
        }
      })

      const duplicatedAssignedIdList = duplicatedAssignedList.map((item) => item.studentId)
      const commonAssignedLists: AssignedStudent[][] = []

      assignLists.forEach((studentList) => {
        const assignArr: AssignedStudent[] = []
        studentList?.forEach((student) => {
          if (duplicatedAssignedIdList.includes(student.studentId)) {
            assignArr.push(student)
          }
        })
        commonAssignedLists.push(assignArr)
      })

      return commonAssignedLists
    }

    modalService.openModal(
      <AssignGroupStudentModal
        title={`학습지 일괄 ${tJosa('출제')}하기`}
        titleDescription={`선택한 학습지를 학생들에게 일괄 ${tJosa('출제')}해보세요.`}
        assignedLists={getCommonAssignedLists(worksheetList)}
        onSubmit={async (studentList) => {
          try {
            onAssignClickTracking?.()
            await lessonWorksheetController.assignStudentWorksheet(
              studentList.map(({ id }) => id),
              worksheetIdList,
              trackingConverter.출제학습지타입(worksheetList),
            )
            fetchInitCallback?.(worksheetIdList)
            toastSuccess(`시험지가 ${tJosa('출제')}되었습니다.`)
            worksheetUtil.isExamOrCategoryWorksheetTag(worksheetList[0].tag) &&
              _mixpanelUtils.uiTrack('테마별학습지_일괄출제', {
                학습지수: worksheetIdList.length,
              })
          } catch (error) {
            commonErrorMessage(error)
          }
        }}
        onUnassign={async (newlyAssignedStudentIds) => {
          await lessonWorksheetController.deleteStudentWorksheets(newlyAssignedStudentIds)
          fetchInitCallback?.(worksheetIdList)
          modalService.closeModal()
          toastSuccess(`시험지가 ${tJosa('출제')} 취소되었습니다.`)
        }}
      />,
    )
  }

  // 학습지 개별 출제
  assignStudentWorksheet = (
    worksheet: Worksheet,
    fetchInitCallback?: (
      worksheetId: Worksheet['id'],
      assignedStudentIds?: Worksheet['assignedStudentIdList'],
      unassignedStudentIds?: Worksheet['assignedStudentIdList'],
    ) => Promise<void> | void,
    onAssignClickTracking?: () => void,
  ) => {
    const assignedLists = [worksheet.assignedStudentWorksheetList || []]

    const assignNow = async (assignedStudents: { id: Student['id']; name: Student['name'] }[]) => {
      try {
        onAssignClickTracking?.()
        await lessonWorksheetController.assignStudentWorksheet(
          assignedStudents.map(({ id }) => id),
          [worksheet.id],
          trackingConverter.출제학습지타입([worksheet]),
        )

        scenarioService.postScenarioAndLoadCompletePopupHanlder('학습지출제하기')

        fetchInitCallback?.(
          worksheet.id,
          assignedStudents.map(({ id }) => id),
          [],
        )
        toastSuccess(TOAST_STR.assignWorksheet)
        _mixpanelUtils.uiTrack('테마별학습지_출제하기', {
          학제: worksheet.schoolType,
          출판사: worksheet?.publisher ?? null,
          학생수: assignedStudents.length,
        })
      } catch (error) {
        if (error.status === 400) {
          modalService.openDeletedBookModal()
        } else {
          commonErrorMessage(error)
        }
        // 실패를 안에서 안내했으니 모달은 닫지 않는다
        return false
      }
    }

    const unassign: ComponentProps<typeof AssignGroupStudentModal>['onUnassign'] = async (
      newlyAssignedStudentIds,
      studentIds,
    ) => {
      try {
        await lessonWorksheetController.deleteStudentWorksheets(newlyAssignedStudentIds)
        fetchInitCallback?.(worksheet.id, [], studentIds)
        toastSuccess(TOAST_STR.unassignWorksheet)
        modalService.closeModal()
        return false
      } catch (error) {
        if (error.status === 400) {
          modalService.openDeletedBookModal()
        } else {
          commonErrorMessage(error)
        }
        // 출제 취소가 안 됐으니 학생을 선택 목록에서 빼지 않는다
        return true
      }
    }

    modalService.openModal(
      commonRepo.isSchoolflat ? (
        <ReservableAssignWorksheetModal
          worksheet={worksheet}
          assignedLists={assignedLists}
          onAssignNow={assignNow}
          onUnassign={unassign}
          onScheduled={() => fetchInitCallback?.(worksheet.id, [], [])}
          onScheduleChanged={() => fetchInitCallback?.(worksheet.id, [], [])}
        />
      ) : (
        <AssignGroupStudentModal
          title={`학습지 ${tJosa('출제')}하기`}
          titleDescription={worksheet.title}
          assignedLists={assignedLists}
          onSubmit={assignNow}
          onUnassign={unassign}
        />
      ),
      {
        onClose: () => {
          fetchInitCallback?.(worksheet.id, [], [])
          return true
        },
      },
    )
  }
}

export const commonWorksheetService = new CommonWorksheet()
